import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Input from '../../components/form-control/Input';
import * as Yup from 'yup';
import Button from '../../components/form-control/Button';
import { Formik, FormikHelpers } from 'formik';
import { BsPerson } from 'react-icons/bs';
import { HiOutlineBuildingLibrary, HiOutlineEnvelope } from "react-icons/hi2";
import SelectDropdown, { ISelectDropdownItem } from '../../components/form-control/SelectDropdown';
import { COUNTRIES, GENDER } from '../../resources/enums';
import { getCountryIsoFromName, getFormattedPhoneNumber, phoneValidationSchema, showToast } from '../../utils/miscFunctions';
import { Building2, Globe, Users, ShieldCheck } from 'lucide-react';
import { REGIONS_BY_COUNTRY } from '../../resources/constants';
import { registerNewAdmin } from '../../api/requests';
import useModal from '../../hooks/modalHook';
import RegistrationSuccess from '../../components/auth/RegistrationSuccess';
import IntlPhoneInput from '../../components/form-control/IntlPhoneInput/IntlPhoneInput';
import { useSelector } from 'react-redux';
import { getEnumsFromState } from '../../states/slices/resourceSlice';
import { IEnum } from '../../resources/interfaces';
import Loader from '../../components/Loader';

const ROLE_OPTIONS = [
    { name: 'Administrator', value: 'Administrator' },
    { name: 'Manager', value: 'Manager' },
    { name: 'Workspace Owner', value: 'Workspace Owner' },
    { name: 'Agent', value: 'Agent' },
];

// Update the form interface
export interface RegisterFormData {
    // Personal Information
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    jobTitle: string;

    // Business Information
    companyName: string;
    companyType: string;
    corporateEmail: string;
    corporatePhone: string;

    // Location
    country: string;
    state: string;
    city: string;
}

const sections: { id: FormSection; title: string }[] = [
    { id: 'personal', title: 'Personal Information' },
    { id: 'business', title: 'Business Information' },
];

type FormSection = 'personal' | 'business';

export const RegisterForm: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [currentSection, setCurrentSection] = useState<FormSection>('personal');
    const [formData, setFormData] = useState<Partial<RegisterFormData>>({});
    const { ModalComponent, showModal, closeModal, updateModalStates } = useModal()
    const enums = useSelector(getEnumsFromState);
    const companyTypes = enums?.companyTypes

    const getInitialValues = () => ({
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || '',
        jobTitle: formData.jobTitle || '',
        companyName: formData.companyName || '',
        companyType: formData.companyType || '',
        corporateEmail: formData.corporateEmail || '',
        corporatePhone: formData.corporatePhone || '',
        country: formData.country || 'Ghana',
        state: formData.state || '',
        city: formData.city || '',
    });

    const onPaymentSuccessful = () => {
        updateModalStates({
            show: true,
            children: <RegistrationSuccess />,
            static: true,
            showCloseButton: false,
            size: "4xl",
        });
    };

    const handleSubmit = async (values: RegisterFormData, { setSubmitting }: FormikHelpers<RegisterFormData>) => {

        const payload = {
            ...values,
            gender: GENDER.UNSPECIFIED,
            phone: getFormattedPhoneNumber(values.phone),
            corporatePhone: getFormattedPhoneNumber(values.corporatePhone),

        }

        try {
            setIsLoading(true);

            const response = await registerNewAdmin(payload);
            if (response) {
             
                onPaymentSuccessful()
            }

        } catch (error: any) {
            showToast.error(error?.description || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
            setSubmitting(false);
        }
    };


    return (
        <>
            {/* Personal Information Section */}
            {currentSection === 'personal' && (
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={Yup.object().shape({
                        firstName: Yup.string().required('First name is required'),
                        lastName: Yup.string().required('Last name is required'),
                        email: Yup.string()
                            .email('Please enter a valid email')
                            .required('Email is required'),
                        phone: phoneValidationSchema(),
                        jobTitle: Yup.string()
                            .oneOf(ROLE_OPTIONS.map((r) => r.value), 'Please select a valid role')
                            .required('Role is required'),
                    })}
                    onSubmit={(values, { setSubmitting }) => {
                        setFormData(prev => ({ ...prev, ...values }));
                        setCurrentSection('business');
                        setSubmitting(false);
                    }}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {

                        useEffect(() => {
                            setFormData(prev => ({ ...prev, ...values }));
                        }, [values]);


                        return (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-4">

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 }}
                                        >
                                            <Input
                                                label="First Name"
                                                name="firstName"
                                                type="text"
                                                placeholder="Enter first name"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.firstName}
                                                prepend={<BsPerson className="w-5 h-5" />}
                                                error={touched?.firstName ? errors.firstName : undefined}
                                                helperText={touched?.firstName ? errors.firstName : undefined}
                                                labelClasses='!text-gray-700'
                                            />
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.15 }} >
                                            <Input
                                                name="lastName"
                                                label="Last Name"
                                                type="text"
                                                prepend={<BsPerson className="w-5 h-5" />}
                                                placeholder="Enter last name"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.lastName}
                                                error={touched?.lastName ? errors.lastName : undefined}
                                                helperText={touched?.lastName ? errors.lastName : undefined}
                                                labelClasses='!text-gray-700'
                                            />
                                        </motion.div>
                                    </div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 }} >
                                        <IntlPhoneInput
                                            label="Phone Number"
                                            onBlur={() => handleBlur({ target: { name: 'phone' } })}
                                            onChange={(phone: string | undefined) => { setFieldValue("phone", phone); }}
                                            placeholder="Enter phone number"
                                            value={values.phone}
                                            error={touched?.phone ? errors.phone : undefined}
                                            helperText={touched?.phone ? errors.phone : undefined}
                                            labelClasses='!text-gray-700'
                                        />
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            label='Email Address'
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="you@company.com"
                                            prepend={<HiOutlineEnvelope className="w-5 h-5" />}
                                            error={touched?.email ? errors.email : undefined}
                                            helperText={touched?.email ? errors.email : undefined}
                                            labelClasses='!text-gray-700'
                                        />
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}>
                                        <SelectDropdown
                                            label="Role"
                                            name="jobTitle"
                                            listSelectedValue={values.jobTitle}
                                            list={ROLE_OPTIONS}
                                            onChange={(val: string | null) => setFieldValue("jobTitle", val)}
                                            onBlur={handleBlur}
                                            placeholder="Select role"
                                            prepend={<ShieldCheck className="w-5 h-5" />}
                                            required
                                            error={touched?.jobTitle ? errors.jobTitle : undefined}
                                            helperText={touched?.jobTitle ? errors.jobTitle : undefined}
                                            labelClasses='!text-gray-700'
                                        />
                                    </motion.div>

                                    <div className="flex justify-end !mt-6 ">
                                        {/* <Button
                                            type="submit"
                                            className="w-full flex justify-center items-center px-6 py-3 !text-lg font-medium rounded-full border border-transparent text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
                                            label="Next"
                                            disabled={isLoading}
                                        /> */}

                                        <button
                                            type="submit"
                                            className="w-full flex justify-center items-center px-6 py-3 !text-lg font-medium rounded-full border border-transparent text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
                                            disabled={isLoading}>
                                            {isLoading ? <Loader /> : "Next"}
                                        </button>

                                    </div>
                                </div>
                            </form>
                        )
                    }}
                </Formik>
            )}

            {currentSection === 'business' && (
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={Yup.object().shape({
                        companyName: Yup.string().required('Organization name is required'),
                        companyType: Yup.string()
                            .oneOf(companyTypes?.map((type: IEnum) => type.key) || [], 'Please select a valid organization type')
                            .required('Organization type is required'),
                        corporateEmail: Yup.string()
                            .email('Please enter a valid corporate email')
                            .when('email', (email, schema) => {
                                if (email) {
                                    return schema.required('Corporate email is required');
                                }
                                return schema;
                            }), corporatePhone: phoneValidationSchema(),
                        country: Yup.string().required('Country is required'),
                        state: Yup.string().when('country', {
                            is: (val: string) => val === 'Ghana',
                            then: (schema) => schema.required('State/Region is required for Ghana')
                        }),
                        city: Yup.string().required('City is required')
                    })}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, validateForm, setTouched, handleSubmit }) => {
                        // Save values to formData when they change
                        useEffect(() => {
                            setFormData(prev => ({ ...prev, ...values }));
                        }, [values]);

                        return (
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {/* Business Information Section */}
                                <div className="space-y-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.25 }}>
                                        <Input
                                            id="companyName"
                                            name="companyName"
                                            label='Organization Name'
                                            type="text"
                                            placeholder="Enter organization name"
                                            value={values.companyName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            prepend={<HiOutlineBuildingLibrary className="w-5 h-5" />}
                                            error={touched?.companyName ? errors.companyName : undefined}
                                            helperText={touched?.companyName ? errors.companyName : undefined}
                                        />
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 }} >
                                        <SelectDropdown
                                            label="Organization Type"
                                            name="companyType"
                                            list={companyTypes?.filter((type: IEnum) => type?.key !== 'INTERNAL')?.map((type: IEnum) => ({ name: type.title, value: type.key, description: type.description }))}
                                            listSelectedValue={values.companyType}
                                            onChange={(val: ISelectDropdownItem | null) => {
                                                setFieldValue('companyType', val);
                                            }}
                                            prepend={<Users className="w-5 h-5" />}
                                            onBlur={handleBlur}
                                            placeholder="Select organization type"
                                            required
                                            error={touched?.companyType ? errors.companyType : undefined}
                                            helperText={touched?.companyType ? errors.companyType : undefined}
                                        />
                                    </motion.div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}>
                                            <Input
                                                label="Corporate Email"
                                                name="corporateEmail"
                                                type="email"
                                                value={values.corporateEmail}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter corporate email"
                                                prepend={<HiOutlineEnvelope className="w-5 h-5" />}
                                                error={touched?.corporateEmail ? errors.corporateEmail : undefined}
                                                helperText={touched?.corporateEmail ? errors.corporateEmail : undefined} />
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}>
                                            <IntlPhoneInput
                                                label="Phone Number"
                                                onBlur={() => handleBlur({ target: { name: 'corporatePhone' } })}
                                                onChange={(phone: string | undefined) => { setFieldValue("corporatePhone", phone); }}
                                                placeholder="Enter phone number"
                                                value={values.corporatePhone}
                                                error={touched?.corporatePhone ? errors.corporatePhone : undefined}
                                                helperText={touched?.corporatePhone ? errors.corporatePhone : undefined}
                                            />
                                        </motion.div>
                                    </div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <SelectDropdown
                                                label="Country"
                                                name="country"
                                                listSelectedValue={values.country}
                                                list={Object.values(COUNTRIES)?.map((country: COUNTRIES, id: number) => ({
                                                    name: country,
                                                    value: country
                                                }))}
                                                onChange={(val: string | null) => setFieldValue('country', val)}
                                                onBlur={handleBlur}
                                                placeholder="Select country"
                                                prepend={<Globe className="w-5 h-5" />}
                                                error={touched?.country ? errors.country : undefined}
                                                helperText={touched?.country ? errors.country : undefined}
                                                required
                                            />

                                            <SelectDropdown
                                                label="Region"
                                                name="state"
                                                listSelectedValue={values.state}
                                                list={REGIONS_BY_COUNTRY[getCountryIsoFromName(values.country)]?.map((region: string, id: number) => ({
                                                    name: (region),
                                                    value: region
                                                }))}
                                                onChange={(val: string | null) => setFieldValue('state', val)}
                                                onBlur={handleBlur}
                                                placeholder="Select team region"
                                                required
                                                prepend={<Globe className="w-5 h-5" />}
                                                error={touched?.state ? errors.state : undefined}
                                                helperText={touched?.state ? errors.state : undefined}
                                            />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}>
                                        <Input
                                            label="City"
                                            name="city"
                                            value={values.city}
                                            placeholder="Enter city"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            prepend={<Building2 className="w-5 h-5" />}
                                            error={touched?.city ? errors.city : undefined}
                                            helperText={touched?.city ? errors.city : undefined} />

                                    </motion.div>

                                    <div className="flex justify-between gap-5 !mt-6">
                                        {/* <Button
                                            type="submit"
                                            className="w-full flex justify-center items-center px-6 py-3 !text-lg font-medium rounded-full border border-transparent text-gray-700 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
                                            label="Back"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, ...values }));
                                                setCurrentSection('personal');
                                            }}
                                            disabled={isLoading}
                                        /> */}

                                        <button
                                            type="submit"
                                            title='Back'
                                            className="w-full flex justify-center items-center px-6 py-3 !text-lg font-medium rounded-full border border-border-gray-100  text-gray-700 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
                                            disabled={isLoading}
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, ...values }));
                                                setCurrentSection('personal');
                                            }}>
                                            Back
                                        </button>

                                        {/* <Button
                                            type="submit"
                                            className="w-full flex justify-center items-center px-6 py-3 !text-lg font-medium rounded-full border border-transparent text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
                                            label={isLoading ? 'Processing...' : 'Submit'}
                                            loading={isLoading}
                                            disabled={isLoading}
                                        /> */}

                                        <button
                                            type="submit"
                                            className="w-full flex justify-center items-center px-6 py-3 !text-lg font-medium rounded-full border border-transparent text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
                                            disabled={isLoading}>
                                            {isLoading ? <Loader /> : 'Submit'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )
                    }}
                </Formik>
            )}
            <ModalComponent />
        </>
    );
};
